import type { FC } from "react";
import { motion, AnimatePresence } from "motion/react";
import { mergeClasses } from "@huub-web/utils";

import {
  type ChildrenProp,
  IconButton,
  Portal,
  transitionDurations,
  useKeys,
} from "@huub-web/ui";

import { useNavigationState } from "../../../providers";
import { elevations } from "@huub-web/ui/theme/css/elevations.css";

import {
  closeButtonStyle,
  compactAgentContainerStyle,
} from "./CompactAgent.css";

type CompactAgentProps = ChildrenProp & {
  disableAgent?: boolean;
};

/**
 * Compact version of the agent component for smaller viewports
 */
export const CompactAgent: FC<CompactAgentProps> = ({
  disableAgent,
  children,
}) => {
  const { isSideContentOpen, setSideContentOpen } = useNavigationState();

  useKeys(["Escape"], () => {
    setSideContentOpen(false);
  });

  if (disableAgent) {
    return null;
  }

  return (
    <Portal>
      <AnimatePresence>
        {isSideContentOpen && (
          <motion.div
            className={mergeClasses(compactAgentContainerStyle(), elevations.l)}
            initial={{ opacity: 0, scale: 0.9, transformOrigin: "top right" }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{
              duration: transitionDurations.s,
              ease: "easeInOut",
            }}
          >
            <IconButton
              onClick={() => setSideContentOpen(false)}
              icon="close"
              size="s"
              rounded
              variant="opaque"
              border="default"
              className={closeButtonStyle}
            />
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </Portal>
  );
};
